/**
 * Suppliers Table Component
 *
 * Displays suppliers in a table format with sorting and action capabilities.
 * Uses the generic Table component with supplier-specific column definitions.
 */

import React from 'react';
import styled from 'styled-components';
import { Table, StatusBadge, BooleanIcon } from '../ui';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { Supplier } from '../../types/api';
import { Column, SortDirection } from '../ui/Table';
import { formatDateShort } from '../../utils/formatters';

// ============================================================================
// Styled Components
// ============================================================================

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: var(--space-2);
  justify-content: center;
  align-items: center;

  /* Mobile: Stack buttons vertically with proper spacing */
  @media (max-width: 767px) {
    flex-direction: column;
    gap: var(--space-3);
    width: 100%;
  }
`;

const ActionButton = styled(Button)`
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-xs);
  white-space: nowrap;

  /* Mobile: Full width with enhanced touch target */
  @media (max-width: 767px) {
    width: 100%;
    min-height: 44px;
    padding: var(--space-3) var(--space-4);
    font-size: var(--font-size-sm);
    justify-content: center;
  }
`;

const DeleteActionButton = styled(ActionButton)`
  background: var(--danger-50);
  color: var(--danger-600);
  border: 1px solid var(--danger-100);

  &:hover:not(:disabled) {
    background: var(--danger-100);
    border-color: var(--danger-100);
  }
`;

// ============================================================================
// Types
// ============================================================================

interface SuppliersTableProps {
  suppliers: Supplier[];
  onEdit: (supplier: Supplier) => void;
  onDelete: (supplier: Supplier) => void;
  loading?: boolean;
  onSort?: (key: keyof Supplier | string, direction: SortDirection) => void;
}

// ============================================================================
// Component
// ============================================================================

export const SuppliersTable: React.FC<SuppliersTableProps> = ({
  suppliers,
  onEdit,
  onDelete,
  loading = false,
  onSort,
}) => {
  // Define table columns with proper typing and rendering
  const columns: Column<Supplier>[] = [
    {
      key: 'name',
      header: 'Nombre',
      sortable: true,
      width: '25%',
      ariaLabel: 'Nombre del proveedor, activar para ordenar alfabéticamente',
      render: (value: string) => (
        <span
          style={{
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-primary)',
          }}
        >
          {value}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      sortable: true,
      width: '15%',
      align: 'center',
      ariaLabel: 'Estado del proveedor, activar para ordenar por estado',
      render: (value: string) => <StatusBadge status={value} />,
    },
    {
      key: 'has_certification',
      header: 'Certificación',
      sortable: true,
      width: '15%',
      align: 'center',
      ariaLabel:
        'Estado de certificación, activar para ordenar por certificación',
      render: (value: boolean) => (
        <BooleanIcon
          value={value}
          trueLabel="Certificado"
          falseLabel="Sin certificación"
        />
      ),
    },
    {
      key: 'description',
      header: 'Descripción',
      sortable: false,
      width: '30%',
      render: (value: string | undefined) => (
        <span
          style={{
            color: value ? 'var(--text-secondary)' : 'var(--text-muted)',
            fontStyle: value ? 'normal' : 'italic',
          }}
          title={value || 'Sin descripción'}
        >
          {value || 'Sin descripción'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Fecha de Creación',
      sortable: true,
      width: '15%',
      align: 'right',
      ariaLabel: 'Fecha de creación, activar para ordenar cronológicamente',
      render: (value: string) => {
        try {
          return (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
              }}
            >
              {formatDateShort(value)}
            </span>
          );
        } catch {
          return <span style={{ color: 'var(--text-muted)' }}>—</span>;
        }
      },
    },
    {
      key: 'actions',
      header: 'Acciones',
      sortable: false,
      align: 'center',
      ariaLabel: 'Acciones disponibles para este proveedor',
      render: (_: any, supplier: Supplier) => (
        <ActionButtonsContainer>
          <ActionButton
            size="sm"
            variant="secondary"
            onClick={() => onEdit(supplier)}
            aria-label={`Editar proveedor ${supplier.name}`}
          >
            <Icon name="edit" size="sm" aria-hidden />
            Editar
          </ActionButton>
          <DeleteActionButton
            size="sm"
            variant="secondary"
            onClick={() => onDelete(supplier)}
            aria-label={`Eliminar proveedor ${supplier.name}`}
          >
            <Icon name="delete" size="sm" aria-hidden />
            Eliminar
          </DeleteActionButton>
        </ActionButtonsContainer>
      ),
    },
  ];

  // Extract unique key for each supplier
  const keyExtractor = (supplier: Supplier) => supplier.id;

  return (
    <Table
      data={suppliers}
      columns={columns}
      keyExtractor={keyExtractor}
      loading={loading}
      {...(onSort && { onSort })}
      initialSort={{
        key: 'name',
        direction: 'asc',
      }}
      emptyMessage="No se encontraron proveedores"
      aria-label="Tabla de proveedores"
      aria-describedby="suppliers-table-description"
    />
  );
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format supplier data for display
 * Useful for preparing data before passing to the table
 */
export const formatSupplierForDisplay = (supplier: Supplier): Supplier => {
  return {
    ...supplier,
    // Ensure dates are properly formatted
    created_at: supplier.created_at,
    updated_at: supplier.updated_at,
  };
};

/**
 * Get suppliers sorted by a specific key and direction
 * Useful for server-side sorting or when external sorting is needed
 */
export const sortSuppliers = (
  suppliers: Supplier[],
  key: keyof Supplier | string,
  direction: SortDirection,
): Supplier[] => {
  if (!direction) return suppliers;

  return [...suppliers].sort((a, b) => {
    const aValue = a[key as keyof Supplier];
    const bValue = b[key as keyof Supplier];

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return direction === 'asc' ? -1 : 1;
    if (bValue == null) return direction === 'asc' ? 1 : -1;

    // Handle different data types
    let comparison = 0;

    switch (key) {
      case 'name':
        comparison = String(aValue).localeCompare(String(bValue));
        break;
      case 'status':
        // Sort active first, then inactive
        const aStatus = aValue === 'active' ? 1 : 0;
        const bStatus = bValue === 'active' ? 1 : 0;
        comparison = aStatus - bStatus;
        break;
      case 'has_certification':
        comparison = (aValue ? 1 : 0) - (bValue ? 1 : 0);
        break;
      case 'created_at':
      case 'updated_at':
        try {
          const aDate = new Date(String(aValue)).getTime();
          const bDate = new Date(String(bValue)).getTime();
          comparison = aDate - bDate;
        } catch {
          comparison = 0;
        }
        break;
      default:
        comparison = String(aValue).localeCompare(String(bValue));
    }

    return direction === 'asc' ? comparison : -comparison;
  });
};

export default SuppliersTable;
